
import { useState, useEffect } from 'react';

export default function WhatsAppScrollPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const windowHeight = window.innerHeight;
      
      // Mostrar el popup cuando haya scrolleado más de 2 pantallas y no se haya mostrado antes
      if (scrolled > windowHeight * 2 && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasShown]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleJoinFounders = () => {
    // Redirigir al enlace de Skool para fundadores
    window.open('https://www.skool.com/experiencia-neo-8010', '_blank');
    setIsVisible(false);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsVisible(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-md z-[99999] flex items-center justify-center px-4 py-8 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[280px] transform animate-slideUp overflow-visible">
        {/* Gradiente superior decorativo */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6607a5] via-purple-500 to-violet-600"></div>
        
        {/* Botón de cerrar */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 w-5 h-5 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer z-10"
        >
          <i className="ri-close-line text-xs text-gray-600"></i>
        </button>

        {/* Badge de urgencia */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-gradient-to-r from-[#6607a5] to-purple-600 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-xl text-center animate-pulse-gentle whitespace-nowrap">
            ⚡ ESTA OFERTA ACABARÁ PRONTO
          </div>
        </div>

        {/* Contenido del popup */}
        <div className="p-4 pt-8">
          {/* Icono central */}
          <div className="w-12 h-12 bg-gradient-to-br from-[#6607a5] to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
            <i className="ri-vip-crown-line text-white text-lg"></i>
          </div>

          {/* Título principal */}
          <h3 className="text-base font-bold text-gray-900 mb-1 text-center">
            ¡ÚLTIMA OPORTUNIDAD!
          </h3>

          {/* Subtítulo */}
          <p className="text-xs text-[#6607a5] font-semibold mb-3 text-center">
            Solo quedan muy pocas plazas de fundador
          </p>

          {/* Oferta destacada */}
          <div className="bg-gradient-to-br from-[#6607a5]/5 to-purple-100 rounded-lg p-3 mb-3 border border-[#6607a5]/20">
            <div className="text-center">
              <div className="text-xs text-gray-500 line-through mb-1">
                Precio regular: $49/mes
              </div>
              <div className="text-xl font-bold text-[#6607a5] mb-1">
                $19/mes
              </div>
              <div className="inline-flex items-center bg-gradient-to-r from-[#6607a5] to-purple-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                <i className="ri-fire-line mr-1"></i>
                PRECIO CONGELADO
              </div>
            </div>
          </div>

          {/* Beneficios exclusivos */}
          <div className="space-y-1.5 mb-3">
            <div className="flex items-center text-xs text-gray-700">
              <div className="w-4 h-4 bg-[#6607a5]/10 rounded-full flex items-center justify-center mr-2">
                <i className="ri-check-line text-[#6607a5] text-xs"></i>
              </div>
              <span>Acceso inmediato a la comunidad</span>
            </div>
            <div className="flex items-center text-xs text-gray-700">
              <div className="w-4 h-4 bg-[#6607a5]/10 rounded-full flex items-center justify-center mr-2">
                <i className="ri-check-line text-[#6607a5] text-xs"></i>
              </div>
              <span>Participas en todas las decisiones</span>
            </div>
            <div className="flex items-center text-xs text-gray-700">
              <div className="w-4 h-4 bg-[#6607a5]/10 rounded-full flex items-center justify-center mr-2">
                <i className="ri-check-line text-[#6607a5] text-xs"></i>
              </div>
              <span>Diagnóstico personal 1:1</span>
            </div>
            <div className="flex items-center text-xs text-gray-700">
              <div className="w-4 h-4 bg-[#6607a5]/10 rounded-full flex items-center justify-center mr-2">
                <i className="ri-check-line text-[#6607a5] text-xs"></i>
              </div>
              <span>Precio fundador para siempre</span>
            </div>
          </div>

          {/* Urgencia */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-2 mb-3">
            <div className="flex items-center justify-center mb-0.5">
              <i className="ri-alarm-warning-line text-red-600 text-xs mr-1"></i>
              <span className="font-bold text-red-700 text-xs">Esta oferta acabará pronto</span>
            </div>
            <div className="text-xs text-red-600 text-center">
              Últimas plazas disponibles, no te quedes sin la tuya
            </div>
          </div>

          {/* CTA Principal */}
          <button
            onClick={handleJoinFounders}
            className="w-full bg-gradient-to-r from-[#6607a5] to-purple-600 hover:from-purple-600 hover:to-[#6607a5] text-white font-bold py-2.5 px-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer text-xs flex items-center justify-center gap-1.5 mb-2 transform hover:scale-105"
          >
            <i className="ri-rocket-line text-sm"></i>
            <span>ASEGURAR MI PLAZA</span>
          </button>

          {/* Garantía */}
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-0.5">
              <i className="ri-shield-check-line mr-1 text-[#6607a5]"></i>
              PAGO 100% SEGURO · Cancela cuando quieras
            </div>
            <div className="text-xs font-bold text-[#6607a5]">
              ⏰ Esta oferta acabará pronto
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes pulseGentle {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.05);
            opacity: 0.9;
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.5s ease-out;
        }
        
        .animate-pulse-gentle {
          animation: pulseGentle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
